import axios from "axios";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Spinner from "~/components/spinner";
import api from "~/services/api";
import useGlobalContext from "~/view-data/use-global-context";
import { MdRotateRight } from "react-icons/md";
import { secondary } from "../mixins/color";

const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${(props) => (props.size ? props.size : "10px")};
  min-width: ${(props) => (props.size ? props.size : "10px")};

  position: relative;

  & img {
    -webkit-transition: all 0.5s ease-in-out;
    -moz-transition: all 0.5s ease-in-out;
    -o-transition: all 0.5s ease-in-out;
    transition: all 0.5s ease-in-out;
  }

  & svg {
    font-size: 30px;
    position: absolute;
    top: 10px;
    right: 10px;
    color: rgba(255, 255, 255, 1);
    padding: 5px;
    background: ${secondary.alpha(0.5).string()};
    color: #000;
    border-radius: 2px;

    &:hover {
      background: #ccc;
      cursor: pointer;
    }
  }

  .label {
    font-size: 17px;
    position: absolute;
    bottom: 10px;
    left: 10px;
    color: rgba(255, 255, 255, 1);
    text-shadow: 0 0 2px #000;
  }
`;

function Image(props) {
  const [loading, setLoading] = useState(false);
  const [withoutLoading] = useState(props.withoutLoading || false);
  const [src, setSrc] = useState(props.defaultImage);
  const [, setAngle] = useState(0);
  const [transformStyle, setTransformStyle] = useState({});
  const unmounted = useRef(false);
  const imageRef = useRef(false);

  const {
    state: { auth = {} },
  } = useGlobalContext();

  if (auth && auth.token) {
    api.defaults.headers.Authorization = `Bearer ${auth.token}`;
  }

  function readFileAsync({ data }) {
    return new Promise((resolve, reject) => {
      let reader = new FileReader();
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(data);
    });
  }

  const load = useCallback((src, sourceToken) => {
    setLoading(true);
    
    if (src && src.indexOf("data:image/") !== -1) {
      if (!unmounted.current) {
        setSrc(src);
        setLoading(false);
      }
    } else {
      api
        .get(src, { responseType: "blob", cancelToken: sourceToken.token })
        .then(readFileAsync)
        .then((result) => {
          if (!unmounted.current) {
            setSrc(result);
            setLoading(false);
          }
        })
        .catch((e) => {
          if (!unmounted.current) {
            setSrc(null);
            setLoading(false);
          }
        });
    }
  }, []);

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    if (props && props.src) {
      load(props.src, source);
    }
    return () => {
      source.cancel();
    };
    // eslint-disable-next-line
  }, [load, props.src]);

  useEffect(() => {
    return () => {
      unmounted.current = false;
    };
  });

  const handleRotate = () => {
    if (imageRef.current) {
      setAngle((previous) => {
        const newValue = previous === 360 ? 0 : previous + 45;
        setTransformStyle({
          transform: `rotate(${newValue}deg)`
        })
        return newValue;
      });
    }
  };

  return (
    <Container size={props.size}>
      <Spinner visible={!withoutLoading && loading} />
      {!loading && (
        <>
          <img src={src || null} alt="img" key={src} ref={imageRef} style={transformStyle} />
          {props.rotate && <MdRotateRight onClick={handleRotate} />}
          <span className="label">{props.alt}</span>
        </>
      )}
    </Container>
  );
}

export default memo(Image);
